<script setup lang="ts">
import type { Diagnostic } from "@/types";
import { computed, ref } from "vue";
import { useGetReportSectionsTree } from "@/stores/queries";
import {
  useGetReportImages,
  useSetReportImageOverview,
  useSetReportImageOverviewSection,
  useCreateReportImage,
} from "@/stores/queries/report-images.query";
import { useMergedReportImages } from "@/composable/useMergedReportImages";
import type { ReportImage } from "@/types/report-images.types";
import ReportContent from "../../layout/ReportContent.vue";
import OverviewDropZone from "./OverviewDropZone.vue";
import GaleryModal from "../../galery/GaleryModal.vue";
import EditReportImageDialog from "../photos/EditReportImageDialog.vue";

const props = defineProps<{
  diagnostic: Diagnostic;
  reportId: number;
}>();

const diagnosticId = computed(() => props.diagnostic.id);
const reportId = computed(() => props.reportId);

const { data: reportImages } = useGetReportImages(reportId);
const { data: sectionsTree } = useGetReportSectionsTree(reportId);

const { mutate: setReportOverview, isPending: isSettingDiagnosticOverview } =
  useSetReportImageOverview(reportId);
const { mutate: setOverviewSection, isPending: isSettingOverviewSection } =
  useSetReportImageOverviewSection(reportId);
const { mutateAsync: createReportImage, isPending: isCreatingReportImage } =
  useCreateReportImage(reportId);

// Use merged images to find reportImageId for virtual images
const { mergedImages } = useMergedReportImages(diagnosticId, reportId);

// Helper to ensure a ReportImage exists and get its ID
const ensureReportImageId = async (
  diagnosticImageId: number,
): Promise<number | null> => {
  const image = mergedImages.value.find(
    (img) => img.diagnosticImageId === diagnosticImageId,
  );
  if (!image) return null;
  if (image.reportImageId) return image.reportImageId;
  // Create the ReportImage if it's virtual
  if (image.isVirtual) {
    const newReportImage = await createReportImage({
      diagnosticImageId,
      reportId: props.reportId,
    });
    return newReportImage.id;
  }
  return null;
};

// Handle drop from drag-and-drop (receives diagnosticImageId)
const handleDropDiagnosticOverview = async (diagnosticImageId: number) => {
  const reportImageId = await ensureReportImageId(diagnosticImageId);
  if (reportImageId) {
    setReportOverview({ imageId: reportImageId, isOverview: true });
  }
};

const handleDropSectionOverview = async (
  diagnosticImageId: number,
  sectionId: number,
) => {
  const reportImageId = await ensureReportImageId(diagnosticImageId);
  if (reportImageId) {
    setOverviewSection({ imageId: reportImageId, sectionId });
  }
};

// Main overview images (isOverview = true)
const mainOverviewImages = computed(() => {
  if (!reportImages.value) return [];
  return reportImages.value.filter((img) => img.isOverview);
});

// Section overview images (grouped by overviewSectionId)
type SectionItem = {
  id: number;
  order: number;
  path?: string;
  title: string;
  children?: SectionItem[];
};

const flattenSections = (items: SectionItem[]): SectionItem[] => {
  const result: SectionItem[] = [];
  for (const item of items) {
    result.push(item);
    if (item.children?.length) {
      result.push(...flattenSections(item.children));
    }
  }
  return result;
};

const flatSections = computed(() => flattenSections(sectionsTree.value || []));

const getSectionOverviews = (sectionId: number) => {
  if (!reportImages.value) return [];
  return reportImages.value.filter(
    (img) => img.overviewSectionId === sectionId,
  );
};

// Gallery modal state
const showGallery = ref(false);
const galleryTarget = ref<
  { type: "diagnostic" } | { type: "section"; sectionId: number } | null
>(null);

const openGalleryForDiagnostic = () => {
  galleryTarget.value = { type: "diagnostic" };
  showGallery.value = true;
};

const openGalleryForSection = (sectionId: number) => {
  galleryTarget.value = { type: "section", sectionId };
  showGallery.value = true;
};

// Handle gallery confirmation - receives reportImageIds from GaleryModal
const handleGalleryConfirm = (reportImageIds: number[]) => {
  if (!galleryTarget.value || reportImageIds.length === 0) return;
  const imageId = reportImageIds[0]; // Take first selected

  if (galleryTarget.value.type === "diagnostic") {
    setReportOverview(
      { imageId, isOverview: true },
      { onSuccess: () => (showGallery.value = false) },
    );
  } else {
    setOverviewSection(
      { imageId, sectionId: galleryTarget.value.sectionId },
      { onSuccess: () => (showGallery.value = false) },
    );
  }
};

const removeDiagnosticOverview = (imageId: number) => {
  setReportOverview({ imageId, isOverview: false });
};

const removeSectionOverview = (imageId: number) => {
  setOverviewSection({ imageId, sectionId: null });
};

// Edit dialog state
const editingImage = ref<ReportImage | null>(null);
const showEditDialog = ref(false);

const openEditDialog = (image: ReportImage) => {
  editingImage.value = image;
  showEditDialog.value = true;
};
</script>

<template>
  <ReportContent>
    <div class="overviews-step w-100" :style="`max-width: 900px`">
      <!-- Vue d'ensemble de l'ouvrage -->
      <div class="mb-5">
        <h5 class="mb-3 d-flex align-items-center gap-2">
          <el-icon><i class="bi-building"></i></el-icon>
          {{ $t("report:overviews.diagnostic.title") }}
        </h5>
        <p class="text-muted small mb-3">
          {{ $t("report:overviews.diagnostic.description") }}
        </p>

        <div class="p-3 bordered shadow-1 rounded bg-white overflow-hidden">
          <OverviewDropZone
            v-loading="isSettingDiagnosticOverview || isCreatingReportImage"
            :images="mainOverviewImages"
            :diagnostic-id="diagnostic.id"
            @add="openGalleryForDiagnostic"
            @remove="removeDiagnosticOverview"
            @edit="openEditDialog"
            @drop="handleDropDiagnosticOverview"
          />
        </div>
      </div>

      <!-- Vues d'ensemble par section -->
      <div>
        <h5 class="mb-3 d-flex align-items-center gap-2">
          <el-icon><i class="bi-diagram-3"></i></el-icon>
          {{ $t("report:overviews.sections.title") }}
        </h5>
        <p class="text-muted small mb-3">
          {{ $t("report:overviews.sections.description") }}
        </p>

        <div v-if="flatSections.length" class="sections-list">
          <div
            v-for="section in flatSections"
            :key="section.id"
            class="section-overview-item mb-4 p-3 bordered shadow-1 rounded bg-white overflow-hidden"
          >
            <h6 class="mb-3">{{ section.path }}. {{ section.title }}</h6>
            <OverviewDropZone
              v-loading="isSettingOverviewSection || isCreatingReportImage"
              :images="getSectionOverviews(section.id)"
              :diagnostic-id="diagnostic.id"
              @add="openGalleryForSection(section.id)"
              @remove="removeSectionOverview"
              @edit="openEditDialog"
              @drop="
                (imageId) => handleDropSectionOverview(imageId, section.id)
              "
            />
          </div>
        </div>

        <div v-else class="text-center text-muted py-4">
          <el-icon size="48" class="mb-2"><i class="bi-diagram-3"></i></el-icon>
          <p>{{ $t("report:overviews.sections.empty") }}</p>
        </div>
      </div>
    </div>
  </ReportContent>

  <GaleryModal
    v-model="showGallery"
    :diagnosticId="diagnostic.id"
    :reportId="reportId"
    :loading="isSettingDiagnosticOverview || isSettingOverviewSection"
    :priority-section-id="
      galleryTarget?.type === 'section' ? galleryTarget.sectionId : undefined
    "
    @confirm="handleGalleryConfirm"
    :title="
      galleryTarget?.type === 'diagnostic'
        ? $t('report:overviews.modal.diagnosticTitle')
        : $t('report:overviews.modal.sectionTitle')
    "
    :message="
      galleryTarget?.type === 'diagnostic'
        ? $t('report:overviews.modal.diagnosticMessage')
        : $t('report:overviews.modal.sectionMessage')
    "
    :confirmLabel="$t('report:overviews.modal.confirm')"
  />

  <!-- Edit report image dialog -->
  <EditReportImageDialog
    v-model="showEditDialog"
    :image="editingImage"
    :report-id="reportId"
  />
</template>

<style lang="scss" scoped>
.overviews-step {
  padding-bottom: 100px;
}

.section-overview-item {
  background-color: var(--bs-light);
}
</style>
